// Crisp canvas on phones: sizes the backing store by devicePixelRatio and tracks resizes.

export interface Surface {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  /** CSS pixel size */
  w: number;
  h: number;
  destroy(): void;
}

export function makeSurface(
  host: HTMLElement,
  opts: { aspect?: number; height?: number; onResize?: (s: Surface) => void; draw?: boolean } = {},
): Surface {
  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  if (opts.draw !== false) canvas.style.touchAction = 'none';
  host.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;
  const s: Surface = { canvas, ctx, w: 0, h: 0, destroy };

  let notified = false;
  function size(notify: boolean) {
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth || host.clientWidth || 380;
    const h = opts.height ?? Math.round(w * (opts.aspect ?? 0.75));
    const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
    // assigning width/height wipes the canvas, so only do it when the size really changed
    const changed = canvas.width !== bw || canvas.height !== bh;
    if (changed) {
      canvas.style.height = h + 'px';
      canvas.width = bw;
      canvas.height = bh;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    s.w = w;
    s.h = h;
    if (!changed && notify && notified) return; // nothing new to tell
    // onResize is never called synchronously from the constructor: callers
    // typically close over the returned surface, which is not assigned yet.
    if (notify) { notified = true; opts.onResize?.(s); }
  }
  const ro = new ResizeObserver(() => size(true));
  ro.observe(host);
  size(false);
  requestAnimationFrame(() => { if (!notified) size(true); });

  function destroy() {
    ro.disconnect();
    canvas.remove();
  }
  return s;
}

export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}
